// TP-25: 決済 / 請求の表示整形（純関数）。saas-admin / tenant-portal の各ページに散っていた
// インライン整形（yen の toLocaleString / カードラベル / status ラベル）を 1 箇所に集約する。
// Web/DOM 非依存の pure 関数のみ（@kawai/shared は functions(Node) からも参照されるため）。
/** 金額（円・整数）を `¥X,XXX` 形式に整形（ja-JP ロケール）。負値も符号付きで返す。 */
export function formatYen(amountYen) {
    return `¥${amountYen.toLocaleString('ja-JP')}`;
}
/**
 * 登録カードのブランド + 下 4 桁ラベル（例: `Visa ****4242`）。
 * カード番号 / トークンは非保持のため、保持メタ（brand / last4）だけで構成する。
 */
export function formatCardLabel(card) {
    return `${card.brand} ****${card.last4}`;
}
/** カード有効期限を `MM/YYYY` 形式に整形（expMonth は 0 埋め 2 桁 / expYear は西暦 4 桁）。 */
export function formatCardExpiry(card) {
    return `${String(card.expMonth).padStart(2, '0')}/${card.expYear}`;
}
/**
 * TP-27/TP-34: 手動消し込みで運用する支払方法の一覧（カード自動課金以外）。
 * 手動請求の横断一覧クエリ（`where('paymentMethod','in',…)`）や UI の対象テナント抽出で使う。
 */
export const OFFLINE_PAYMENT_METHODS = [
    'bank_transfer',
    'direct_debit',
];
/**
 * TP-27/TP-34: 手動請求（銀行振込・口座振替）の支払方法かを判定する type guard。
 * `Tenant.paymentMethod` は未設定（undefined = card 相当）があり得るため optional を受ける。
 */
export function isOfflinePaymentMethod(method) {
    return method === 'bank_transfer' || method === 'direct_debit';
}
/** 支払方法（カード / 銀行振込 / 口座振替）の日本語ラベル。 */
export function paymentMethodLabel(method) {
    switch (method) {
        case 'card':
            return 'カード';
        case 'bank_transfer':
            return '銀行振込';
        case 'direct_debit':
            return '口座振替';
    }
}
/** 請求種別（初期費用 / 月額 / その他）の日本語ラベル。 */
export function billingChargeKindLabel(kind) {
    switch (kind) {
        case 'initial':
            return '初期費用';
        case 'monthly':
            return '月額';
        case 'other':
            return 'その他';
    }
}
/** 請求ステータス（保留 / 成功 / 失敗 / 返金）の日本語ラベル。 */
export function billingChargeStatusLabel(status) {
    switch (status) {
        case 'pending':
            return '保留';
        case 'succeeded':
            return '成功';
        case 'failed':
            return '失敗';
        case 'refunded':
            return '返金';
    }
}
/** サブスクリプション状態（未開始 / トライアル / 稼働中 / 解約 / 停止）の日本語ラベル。 */
export function billingSubscriptionStatusLabel(status) {
    switch (status) {
        case 'none':
            return '未開始';
        case 'trial':
            return 'トライアル';
        case 'active':
            return '稼働中';
        case 'canceled':
            return '解約';
        case 'paused':
            return '停止';
    }
}
/**
 * 手動請求 charge の表示ステータスを導出する（銀行振込・口座振替共用）。
 * `today`（'yyyy-MM-dd' / JST）を注入して純関数化（滞納判定は dueDate < today ＝
 * 期限日いっぱいまでは「請求済」）。dueDate 未設定の pending は滞納判定できず「請求済」扱い。
 */
export function resolveBankTransferDisplayStatus(status, dueDate, today) {
    switch (status) {
        case 'pending':
            return dueDate != null && dueDate < today ? 'overdue' : 'invoiced';
        case 'succeeded':
            return 'paid';
        case 'failed':
            return 'canceled';
        case 'refunded':
            return 'refunded';
    }
}
/**
 * 手動請求（銀行振込・口座振替）の表示ステータス日本語ラベル。
 * `method` を渡すと paid を方式に合わせて「入金済」（銀行振込）/「引落済」（口座振替）で返す
 * （TP-34 / 未指定は従来どおり「入金済」）。
 */
export function bankTransferDisplayStatusLabel(status, method) {
    switch (status) {
        case 'invoiced':
            return '請求済';
        case 'overdue':
            return '滞納';
        case 'paid':
            return method === 'direct_debit' ? '引落済' : '入金済';
        case 'canceled':
            return '取消';
        case 'refunded':
            return '返金';
    }
}
/** 月の日数（うるう年対応）。month は 1-12。 */
function daysInMonth(year, month) {
    // 翌月 0 日 = 当月末日の日数を Date で求めず純算術で出す（TZ 非依存）。
    const lengths = [31, 28, 31, 30, 31, 30, 31, 31, 30, 31, 30, 31];
    if (month === 2) {
        const leap = (year % 4 === 0 && year % 100 !== 0) || year % 400 === 0;
        return leap ? 29 : 28;
    }
    return lengths[month - 1] ?? 31; // month は呼び出し側で 1-12 検証済み（fallback は到達しない）
}
/**
 * 指定月（'yyyy-MM'）の**翌月末日**を返す（契約 第6条3項: 当月分を翌月末日まで後払）。
 * 例: '2026-07' → '2026-08-31' / '2026-12' → '2027-01-31'。
 * 振込請求の入金期限（dueDate）の既定値に使う（純算術 / TZ 非依存）。
 */
export function endOfNextMonth(month) {
    const m = /^(\d{4})-(\d{2})$/.exec(month);
    if (!m) {
        throw new Error(`月の形式が不正です（yyyy-MM）: ${month}`);
    }
    let year = Number(m[1]);
    let mon = Number(m[2]);
    if (mon < 1 || mon > 12) {
        throw new Error(`月の形式が不正です（yyyy-MM）: ${month}`);
    }
    mon += 1;
    if (mon === 13) {
        year += 1;
        mon = 1;
    }
    const day = daysInMonth(year, mon);
    return `${year}-${String(mon).padStart(2, '0')}-${String(day).padStart(2, '0')}`;
}
/**
 * 手動請求（銀行振込・口座振替）の支払期限の既定値。
 * - monthly: 請求対象月（billingMonth）の翌月末日（契約 第6条3項）
 * - initial / other: 発行日の属する月の翌月末日（契約 別紙1: 請求書受領月の翌月末日まで）
 */
export function defaultBankTransferDueDate(args) {
    if (args.kind === 'monthly' && args.billingMonth) {
        return endOfNextMonth(args.billingMonth);
    }
    return endOfNextMonth(args.issuedDate.slice(0, 7));
}
//# sourceMappingURL=billing.js.map